import Highcharts from 'highcharts';
import HighchartsStock from 'highcharts/highstock';

// Highcharts v11 changed a large number of default colours and fonts: it swapped the
// series palette, darkened axis chrome, and moved text from absolute px to em relative
// to a 1rem container (the app root is 62.5%, so 1rem = 10px and v11 text renders ~35%
// smaller). Every value here is pinned to what Highcharts 10.3.3 actually rendered, so
// the upgrade lands with no visual change. This is temporary scaffolding — the
// typography follow-up replaces it with the Roche design tokens.
const V10_PALETTE = [
  '#7cb5ec',
  '#434348',
  '#90ed7d',
  '#f7a35c',
  '#8085e9',
  '#f15c80',
  '#e4d354',
  '#2b908f',
  '#f45b5b',
  '#91e8e1',
];

const V10_DATA_LABEL_FONT_SIZE = '11px';
const V11_DATA_LABEL_FONT_SIZE = '0.7em';

const V10_AXIS = {
  lineColor: '#ccd6eb',
  tickColor: '#ccd6eb',
  labels: { style: { color: '#666666', fontSize: '11px' } },
  title: { style: { fontSize: '12px' } },
};

// v10 dropped the dot after the day number on datetime axis ticks; v11 did not.
const V10_AXIS_DATE_FORMATS = { day: { main: '%e. %b' }, week: { main: '%e. %b' } };

// v11 slowed every state transition from 50ms to 150ms. Applied through
// plotOptions.series so it reaches every series type, including the marker states.
const V10_STATE_ANIMATION = {
  states: { hover: { animation: { duration: 50 } }, inactive: { animation: { duration: 50 } } },
  marker: { states: { hover: { animation: { duration: 50 } } } },
};

// v11 rebuilt the tooltip header at 0.8em. Pinned per series type because the point-marker
// variant below is a different string from the core one, and setting it on
// plotOptions.series would give categorised series the wrong header.
const V10_POINT_TOOLTIP = {
  tooltip: {
    headerFormat: '<span style="color:{point.color}">●</span> <span style="font-size: 10px"> {series.name}</span><br/>',
  },
};

// Not pinned, deliberately:
// - global.buttonTheme.style.fontSize (v10 12px, v11 0.8em) styles range-selector,
//   exporting and drilldown buttons. The app renders none of them: no rangeSelector,
//   and exporting is disabled everywhere. Highcharts also types this as `number` while
//   the runtime needs a string, and a number is silently ignored.
// - colorAxis.title.style (v10 inherited 12px, v11 0.8em) is absent from v11's
//   ColorAxisOptions type, and none of the three colorAxis charts set a title.
// - rangeSelector.inputDateFormat (v10 '%b %e, %Y', v11 '%e %b %Y') — no chart renders a
//   range selector.
// - plotOptions.map/mapline.linecap (v10 'butt', v11 'round'). Still honoured at runtime
//   but no longer in v11's types, and it only changes the cap of a 1px map border.
// - plotOptions.pie.dataLabels.allowOverlap (v10 true, v11 absent). Also gone from v11's
//   types, and v11 falls back to `distance > 0`, which is already true for the default
//   outside pie labels — so the rendered behaviour matches without pinning it.
//
// No option behind them — v11 renderer changes, confirmed by rendering both versions side by
// side and diffing the SVG:
// - columns and legend symbols are emitted as zero-radius rounded-rect paths, not rects. Not a
//   visual difference: v11 hardcodes shapeType to 'roundedRect' and the arcs are degenerate, so
//   the path traces the same box the rect did.
// - the navigator outline and scrollbar thumb are laid out a few px differently
//
// v11 also routed every crisp-edge calculation through one shared crisp() helper in
// Core/Utilities.js. Three call sites drifted from v10, and only the first is pinned — see
// v10CrispCol below:
// - ColumnSeries#crispCol: for an even border width v10 added 1 to the top and bottom edge and
//   v11 rounds, so every bar sat 1px low. Systematic, visible on every column chart, pinned.
// - SVGRenderer#crispLine (grid lines, ticks, axis lines): v10 was round(v) ± mod, v11 is
//   round(v − mod) + mod. Diverges by 1px only when the raw coordinate is fractional, and in
//   either direction. Not pinned — data dependent, and it would double the private-API surface
//   to re-verify per upgrade.
// - SVGElement#crisp (plot border/background, legend box): v10 floored, v11 rounds all four
//   corners. Same reasoning; these rects sit on whole pixels in practice.
const V10_CORE: Highcharts.Options = {
  colors: V10_PALETTE,
  chart: {
    borderColor: '#335cad',
    style: {
      fontFamily: '"Lucida Grande", "Lucida Sans Unicode", Arial, Helvetica, sans-serif',
      fontSize: '12px',
    },
  },
  title: { style: { fontSize: '18px', fontWeight: 'normal' } },
  subtitle: { style: { fontSize: '12px' } },
  caption: { style: { fontSize: '12px' } },
  credits: { style: { fontSize: '9px' } },
  legend: {
    itemStyle: { fontSize: '12px', fontWeight: 'bold' },
    // v11 rounded the legend symbol — most visibly the colour-axis gradient bar — by 3px.
    symbolRadius: 0,
    // v11 added a 2px margin above and below every item and struck hidden items through.
    itemMarginTop: 0,
    itemMarginBottom: 0,
    itemHiddenStyle: { color: '#cccccc', textDecoration: 'none' },
    navigation: { activeColor: '#003399', style: { fontSize: '12px' } },
    title: { style: { fontSize: '12px' } },
    bubbleLegend: { labels: { style: { fontSize: '10px' } } },
  },
  tooltip: {
    backgroundColor: 'rgba(247,247,247,0.85)',
    borderWidth: 1,
    style: { fontSize: '12px', whiteSpace: 'nowrap' },
    headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
    // v11 reordered day and month in every datetime format.
    dateTimeLabelFormats: {
      millisecond: '%A, %b %e, %H:%M:%S.%L',
      second: '%A, %b %e, %H:%M:%S',
      minute: '%A, %b %e, %H:%M',
      hour: '%A, %b %e, %H:%M',
      day: '%A, %b %e, %Y',
      week: 'Week from %A, %b %e, %Y',
    },
  },
  // v11 replaced v10's per-side label offsets with a single `labels.distance`, and set it
  // to 15. On the y axis that lands the labels exactly where v10 put them, but on the x
  // axis it reserves 7px more below the plot, shrinking every chart. 8 reproduces v10.
  xAxis: {
    ...V10_AXIS,
    labels: { ...V10_AXIS.labels, distance: 8 },
    dateTimeLabelFormats: V10_AXIS_DATE_FORMATS,
  },
  yAxis: { ...V10_AXIS, stackLabels: { style: { fontSize: '11px' } } },
  colorAxis: {
    minColor: '#e6ebf5',
    maxColor: '#003399',
    gridLineColor: '#e6e6e6',
    lineColor: '#ccd6eb',
    tickColor: '#ccd6eb',
    labels: { style: { color: '#666666', fontSize: '11px' } },
  },
  // plotOptions.series wins the merge over the per-type defaults for most options, while
  // still letting a chart's own plotOptions[type] override it. Data labels are the one
  // exception — see pinDataLabelFontSize below, which is what actually restores their size.
  plotOptions: {
    series: {
      dataLabels: { style: { fontSize: V10_DATA_LABEL_FONT_SIZE } },
      // v10 fell back to 16 in code when no series set a tooltip distance; v11 ships 6 as
      // a default, moving every tooltip 10px closer to its point.
      tooltip: { distance: 16 },
      ...V10_STATE_ANIMATION,
    },
    // v11 rounded column and bar corners by 3px.
    column: { borderRadius: 0 },
    bar: { borderRadius: 0 },
    // v11 dropped the hover brightness and the explicit halo-off, and added a 2px #666666
    // hover border. Repeating the normal border under states.hover is how v10 looked: it
    // simply never restyled the border on hover.
    map: {
      borderColor: '#cccccc',
      states: { hover: { brightness: 0.2, halo: null, borderColor: '#cccccc', borderWidth: 1 } },
      ...V10_POINT_TOOLTIP,
    },
    mapline: {
      borderColor: '#cccccc',
      states: { hover: { brightness: 0.2, halo: null, borderColor: '#cccccc', borderWidth: 1 } },
      ...V10_POINT_TOOLTIP,
    },
    // v11 changed pie connectors from a straight fixed offset to a crooked line.
    pie: { dataLabels: { connectorShape: 'fixedOffset', crookDistance: '70%' } },
    heatmap: V10_POINT_TOOLTIP,
    scatter: V10_POINT_TOOLTIP,
    bubble: V10_POINT_TOOLTIP,
    mapbubble: V10_POINT_TOOLTIP,
    mappoint: V10_POINT_TOOLTIP,
    flags: { states: { hover: { fillColor: '#ccd6eb' } }, style: { fontSize: '11px' } },
  },
  noData: { style: { fontSize: '12px' } },
  navigation: {
    menuItemStyle: { fontSize: '11px' },
    menuItemHoverStyle: { background: '#335cad', color: '#ffffff' },
  },
  mapNavigation: { buttonOptions: { style: { fontSize: '15px' } } },
  accessibility: { keyboardNavigation: { focusBorder: { style: { color: '#335cad' } } } },
  // v11 restyled the scrollbar into a shorter, rounded, button-less pill. `margin` is not
  // pinned: v10 defaulted it to 10 but v11 applies it differently, and re-adding it moves
  // the navigator 10px further from where v10 put it rather than closer.
  scrollbar: {
    height: 14,
    buttonsEnabled: true,
    barBorderRadius: 0,
    barBorderWidth: 1,
    trackBorderRadius: 0,
    rifleColor: '#333333',
    trackBackgroundColor: '#f2f2f2',
    trackBorderColor: '#f2f2f2',
  },
};

// Highstock ships different defaults from core: its title renders at 16px rather than
// 18px, and it owns the navigator and range selector. Reusing V10_CORE verbatim would
// make stock titles larger than they were on v10.
const V10_STOCK: Highcharts.Options = {
  ...V10_CORE,
  title: { style: { fontSize: '16px', fontWeight: 'normal', color: '#333333' } },
  navigator: {
    outlineColor: '#cccccc',
    maskFill: 'rgba(102,133,194,0.3)',
    // v11 fades the navigator labels to 0.6 and adds a contrast outline.
    xAxis: { labels: { style: { color: '#999999', fontSize: '11px', opacity: 1, textOutline: 'none' } } },
  },
  rangeSelector: {
    inputStyle: { color: '#335cad', fontSize: '12px' },
    labelStyle: { color: '#666666', fontSize: '12px' },
  },
};

//  Workaround for Highcharts v11.4.x regression (Issue #21701):
//  In scrollablePlotArea, standard 'circle' markers (rendered as SVG <circle> tags)
//  fail to render or disappear upon scrolling. Other marker symbols (rendered as SVG
//  <path> tags) work perfectly.

//  Here register a custom symbol 'lollipop-circle' that renders a circle as an SVG path
//  instead of a <circle> element. This bypasses the rendering bug completely while
//  retaining the correct visual appearance.

export const LOLLIPOP_CIRCLE = 'lollipop-circle';

function lollipopCircle(x: number, y: number, w: number, h: number): Highcharts.SVGPathArray {
  const radiusX = w / 2;
  const radiusY = h / 2;
  const centreX = x + radiusX;
  const centreY = y + radiusY;

  return [
    ['M', centreX - radiusX, centreY],
    ['A', radiusX, radiusY, 0, 1, 1, centreX + radiusX, centreY],
    ['A', radiusX, radiusY, 0, 1, 1, centreX - radiusX, centreY],
    ['Z'],
  ];
}

function registerLollipopCircleSymbol(): void {
  Highcharts.SVGRenderer.prototype.symbols[LOLLIPOP_CIRCLE] = lollipopCircle;
  HighchartsStock.SVGRenderer.prototype.symbols[LOLLIPOP_CIRCLE] = lollipopCircle;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

// Series classes need the same treatment as plain objects below, and `typeof aClass` is
// 'function', which isRecord deliberately rejects.
function isObjectLike(value: unknown): value is Record<string, unknown> {
  return (typeof value === 'object' || typeof value === 'function') && value !== null;
}

interface ColumnCrispContext {
  borderWidth?: number;
  options?: { crisp?: boolean };
}

interface CrispRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Highcharts 10.3.3's ColumnSeries#crispCol, minus its dead VML branch. v11 rewrote this around
// a shared crisp() helper (Core/Utilities.js) that rounds to the nearest whole pixel, dropping
// v10's `yCrisp = borderWidth % 2 ? 0.5 : 1`. With an even border width — every column chart
// here sets borderWidth: 0 — v10 added 1 to both the top and the bottom edge, so v11 draws each
// bar 1px higher at an unchanged height: it now sits on the zero line instead of overlapping it.
// No option controls this, hence the prototype override. Re-check on each Highcharts upgrade.
function v10CrispCol(this: ColumnCrispContext, x: number, y: number, width: number, height: number): CrispRect {
  const borderWidth = this.borderWidth ?? 0;
  const xCrisp = -(borderWidth % 2 ? 0.5 : 0);
  const yCrisp = borderWidth % 2 ? 0.5 : 1;

  let crispX = x;
  let crispWidth = width;
  if (this.options?.crisp) {
    const right = Math.round(x + width) + xCrisp;
    crispX = Math.round(x) + xCrisp;
    crispWidth = right - crispX;
  }

  const bottom = Math.round(y + height) + yCrisp;
  // #4504, #4656 — tested against the pre-rounding y, as v10 did.
  const fromTop = Math.abs(y) <= 0.5 && bottom > 0.5;
  let crispY = Math.round(y) + yCrisp;
  let crispHeight = bottom - crispY;

  // #5146 — a bar reaching the top of the plot keeps its edge, v11 dropped this.
  if (fromTop && crispHeight) {
    crispY -= 1;
    crispHeight += 1;
  }

  return { x: crispX, y: crispY, width: crispWidth, height: crispHeight };
}

// `bar` and `columnrange` reach crispCol through the prototype chain and need nothing, but
// `lollipop` and `dumbbell` copy the function by reference at module load
// (`crispCol: colProto.crispCol`), so a module registered before this runs would keep v11's.
// Patching every prototype that owns a crispCol covers both orderings. The registry is read off
// the namespace rather than passed in, because v11's types omit `seriesTypes`.
function pinColumnCrisp(instance: unknown): void {
  if (!isObjectLike(instance)) return;

  const seriesTypes = instance.seriesTypes;
  if (!isObjectLike(seriesTypes)) return;

  Object.values(seriesTypes).forEach(SeriesClass => {
    if (!isObjectLike(SeriesClass)) return;

    const seriesPrototype = SeriesClass.prototype;
    if (!isObjectLike(seriesPrototype)) return;

    if (Object.prototype.hasOwnProperty.call(seriesPrototype, 'crispCol')) {
      seriesPrototype.crispCol = v10CrispCol;
    }
  });
}

// Data labels ignore the plotOptions.series pin above, so they need patching per series type.
// Every type builds its defaults as `merge(Series.defaultOptions, …)` at module load, which
// snapshots the base dataLabels.style — v11 changed its fontSize from 11px to 0.7em — and
// registerSeriesType drops that snapshot straight into defaultOptions.plotOptions[type].
// DataLabel.mergedDataLabelOptions then merges plotOptions[type].dataLabels *over*
// plotOptions.series.dataLabels, the reverse of the precedence Series.setOptions uses. So the
// snapshot wins over anything set on plotOptions.series, both from this file and from a
// chart's own options. Rewriting each snapshot in place is the only pin that reaches a
// rendered label. Guarded on the exact v11 value so the pass is idempotent.
function pinDataLabelFontSize(defaults: Highcharts.Options): void {
  const { plotOptions } = defaults;
  if (!isRecord(plotOptions)) return;

  Object.values(plotOptions).forEach(typeOptions => {
    if (!isRecord(typeOptions)) return;

    const { dataLabels } = typeOptions;
    const perLabelOptions: unknown[] = Array.isArray(dataLabels) ? dataLabels : [dataLabels];

    perLabelOptions.forEach(labelOptions => {
      if (!isRecord(labelOptions)) return;

      const { style } = labelOptions;
      if (isRecord(style) && style.fontSize === V11_DATA_LABEL_FONT_SIZE) {
        style.fontSize = V10_DATA_LABEL_FONT_SIZE;
      }
    });
  });
}

// highcharts and highcharts/highstock are separate modules with their own
// defaultOptions, so both instances need the defaults applied.
export function setupHighcharts(): void {
  Highcharts.setOptions(V10_CORE);
  HighchartsStock.setOptions(V10_STOCK);

  registerLollipopCircleSymbol();

  pinDataLabelFontSize(Highcharts.defaultOptions);
  pinDataLabelFontSize(HighchartsStock.defaultOptions);
  pinColumnCrisp(Highcharts);
  pinColumnCrisp(HighchartsStock);

  // The passes above only reach the types the core bundle has registered by now. Modules like
  // lollipop, dumbbell, heatmap, sunburst, networkgraph and map register theirs when the
  // component that imports them loads, which is long after this runs from src/index.tsx.
  // Chart's `init` event fires before Chart.init merges defaultOptions into the chart options,
  // so re-running them there catches those types in time for the chart being created.
  Highcharts.addEvent(Highcharts.Chart, 'init', () => {
    pinDataLabelFontSize(Highcharts.defaultOptions);
    pinColumnCrisp(Highcharts);
  });
  HighchartsStock.addEvent(HighchartsStock.Chart, 'init', () => {
    pinDataLabelFontSize(HighchartsStock.defaultOptions);
    pinColumnCrisp(HighchartsStock);
  });
}
